import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Account, ConverterType, CurrencyType } from '../types';

/**
 * Account-specific configuration that gets cached and restored
 */
export interface AccountConfiguration {
  accountId: string;
  accountName: string;
  // Country and Bank selection
  selectedCountry: string | null;
  selectedBank: string | null;
  selectedBankName: string | null;
  // Converter settings
  activeConverter: ConverterType;
  targetCurrency: CurrencyType;
  sourceCurrency: CurrencyType;
  ramiTargetCurrency: CurrencyType;
  startDate: string;
  // Categorization settings
  enableCategorization: boolean;
  // Timestamps
  lastUsed: number;
  createdAt: number;
}

/**
 * Default configuration for new accounts
 */
const createDefaultConfiguration = (account: Account, defaultCurrency?: string): AccountConfiguration => ({
  accountId: account.id,
  accountName: account.name,
  selectedCountry: null,
  selectedBank: null,
  selectedBankName: null,
  activeConverter: '',
  targetCurrency: (defaultCurrency as CurrencyType) || 'EUR',
  sourceCurrency: (defaultCurrency as CurrencyType) || 'EUR',
  ramiTargetCurrency: 'SAR',
  startDate: '',
  enableCategorization: false,
  lastUsed: Date.now(),
  createdAt: Date.now(),
});

/**
 * Hook for managing account-specific configurations with persistence
 */
export const useAccountConfiguration = () => {
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Cache key for account configurations
  const ACCOUNT_CONFIG_CACHE_KEY = 'account-configurations';

  // Load all account configurations from React Query cache
  const { data: accountConfigs = {} } = useQuery<Record<string, AccountConfiguration>>({
    queryKey: [ACCOUNT_CONFIG_CACHE_KEY],
    queryFn: () => {
      // Initialize with empty object if no cached data exists
      const savedConfigs = localStorage.getItem('NIMAH_ACCOUNT_CONFIGS');
      return savedConfigs ? JSON.parse(savedConfigs) : {};
    },
    staleTime: Infinity, // Never consider this stale
    gcTime: Infinity, // Never garbage collect
  });

  // Get configuration for a specific account
  const getAccountConfiguration = useCallback((account: Account, defaultCurrency?: string): AccountConfiguration => {
    if (accountConfigs[account.id]) {
      // Update account name in case it changed
      return {
        ...accountConfigs[account.id],
        accountName: account.name,
        lastUsed: Date.now(),
      };
    }
    return createDefaultConfiguration(account, defaultCurrency);
  }, [accountConfigs]);

  // Get configuration for the currently selected account
  const getCurrentConfiguration = useCallback((): AccountConfiguration | null => {
    if (!selectedAccountId || !accountConfigs[selectedAccountId]) {
      return null;
    }
    return accountConfigs[selectedAccountId];
  }, [selectedAccountId, accountConfigs]);

  // Save configuration for an account
  const saveAccountConfiguration = useCallback((config: AccountConfiguration) => {
    const updatedConfigs = {
      ...accountConfigs,
      [config.accountId]: {
        ...config,
        lastUsed: Date.now(),
      },
    };

    // Update React Query cache
    queryClient.setQueryData([ACCOUNT_CONFIG_CACHE_KEY], updatedConfigs);
    
    // Also save to localStorage for persistence across sessions
    localStorage.setItem('NIMAH_ACCOUNT_CONFIGS', JSON.stringify(updatedConfigs));
    
    console.log(`💾 [Account Config] Saved configuration for account: ${config.accountName} (ID: ${config.accountId})`);
    console.log(`📊 [Account Config] Current saved configs:`, Object.keys(updatedConfigs).map(id => ({
      id,
      name: updatedConfigs[id].accountName,
      country: updatedConfigs[id].selectedCountry,
      bank: updatedConfigs[id].selectedBank,
      converter: updatedConfigs[id].activeConverter
    })));
  }, [accountConfigs, queryClient]);

  // Update specific fields of the current account's configuration
  const updateAccountConfiguration = useCallback((
    updates: Partial<Omit<AccountConfiguration, 'accountId' | 'accountName' | 'createdAt'>>
  ) => {
    if (!selectedAccountId) {
      console.warn('⚠️ [Account Config] No account selected for configuration update');
      return;
    }

    const currentConfig = accountConfigs[selectedAccountId];
    if (!currentConfig) {
      console.warn(`⚠️ [Account Config] No configuration found for selected account: ${selectedAccountId}`);
      return;
    }

    const updatedConfig: AccountConfiguration = {
      ...currentConfig,
      ...updates,
      lastUsed: Date.now(),
    };

    console.log(`🔧 [Account Config] Updating configuration for account: ${currentConfig.accountName} (ID: ${selectedAccountId})`);
    console.log(`📝 [Account Config] Changes:`, updates);
    saveAccountConfiguration(updatedConfig);
  }, [selectedAccountId, accountConfigs, saveAccountConfiguration]);

  // Select an account and return its configuration
  const selectAccount = useCallback((account: Account, defaultCurrency?: string): AccountConfiguration => {
    setSelectedAccountId(account.id);
    
    let config = getAccountConfiguration(account, defaultCurrency);
    
    // If this is a new account (no existing config), save the default
    if (!accountConfigs[account.id]) {
      console.log(`🆕 [Account Config] Creating new configuration for account: ${account.name} (ID: ${account.id})`);
      saveAccountConfiguration(config);
    } else {
      console.log(`🔄 [Account Config] Loading existing configuration for account: ${account.name} (ID: ${account.id})`);
      console.log(`📋 [Account Config] Restored settings:`, {
        country: config.selectedCountry,
        bank: config.selectedBank,
        converter: config.activeConverter,
        targetCurrency: config.targetCurrency,
        enableCategorization: config.enableCategorization
      });
      // Update last used timestamp
      config = { ...config, lastUsed: Date.now() };
      saveAccountConfiguration(config);
    }

    console.log(`🎯 [Account Config] Selected account: ${account.name} - restored configuration`);
    return config;
  }, [getAccountConfiguration, accountConfigs, saveAccountConfiguration]);

  // Deselect the current account
  const deselectAccount = useCallback(() => {
    setSelectedAccountId(null);
    console.log('🔚 [Account Config] Account deselected');
  }, []);

  // Get recently used accounts (sorted by last used)
  const getRecentlyUsedAccounts = useCallback((): AccountConfiguration[] => {
    return Object.values(accountConfigs)
      .sort((a: AccountConfiguration, b: AccountConfiguration) => b.lastUsed - a.lastUsed)
      .slice(0, 5); // Return top 5 most recent
  }, [accountConfigs]);

  // Clear all account configurations
  const clearAllConfigurations = useCallback(() => {
    queryClient.setQueryData([ACCOUNT_CONFIG_CACHE_KEY], {});
    localStorage.removeItem('NIMAH_ACCOUNT_CONFIGS');
    setSelectedAccountId(null);
    console.log('🗑️ [Account Config] Cleared all configurations');
  }, [queryClient]);

  return {
    // Current selection
    selectedAccountId,
    currentConfiguration: getCurrentConfiguration(),
    
    // Account management
    selectAccount,
    deselectAccount,
    getAccountConfiguration,
    
    // Configuration updates
    updateAccountConfiguration,
    saveAccountConfiguration,
    
    // Utility methods
    getRecentlyUsedAccounts,
    clearAllConfigurations,
    
    // All configurations
    allConfigurations: accountConfigs,
  };
}; 